"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useLang, LANGS } from "@/lib/i18n";

export default function LangToggle({
  className = "",
  menuPlacement = "bottom",
}: {
  className?: string;
  menuPlacement?: "bottom" | "top";
}) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Language"
        className="flex h-9 items-center gap-1.5 rounded-full border border-[var(--af-border)] px-2.5 text-sm text-[var(--af-text)] transition hover:border-[var(--af-accent)]"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={current.flag} alt="" className="h-4 w-[22px] rounded-sm object-cover" />
        <span className="hidden text-xs font-medium uppercase sm:inline">{current.code}</span>
        <ChevronDown size={13} className="text-[var(--af-muted)]" />
      </button>

      {open && (
        <div
          className={`absolute z-50 w-44 overflow-hidden rounded-xl border border-[var(--af-border)] bg-[var(--af-glass-strong)] py-1 shadow-lg backdrop-blur ${
            menuPlacement === "top" ? "bottom-full left-0 mb-2" : "right-0 top-full mt-2"
          }`}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition hover:bg-[var(--af-accent)]/10 ${
                l.code === lang ? "text-[var(--af-accent)]" : "text-[var(--af-text)]"
              }`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={l.flag} alt="" className="h-4 w-[22px] rounded-sm object-cover" />
              <span className="flex-1">{l.label}</span>
              {l.code === lang && <span className="text-xs">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
